import { IonIcon } from "@ionic/react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  closeOutline,
  copyOutline,
  locationOutline,
  logoWhatsapp,
  navigateOutline,
  openOutline,
} from "ionicons/icons";
import { toast } from "sonner";
import { LinkedText } from "@/components/atoms/linked-text";
import { ModalSheetAutoHeight } from "@/components/modal/sheet/auto-height";
import { ActionButton } from "@/components/molecules/action-button";
import { formatFullAddress } from "@/lib/address";
import { api } from "@/lib/backend";
import { type AdditionalField } from "@/lib/backend/api";
import { Clipboard } from "@/lib/capacitor/clipboard";
import { openNativeNavigation } from "@/lib/utils/navigation";
import { openExternalUrl } from "@/lib/utils/open-url";
import { logger } from "@/logger";
import { getAdditionalFieldIcon } from "@/utils/additional-field-icons";

interface ProjectInfoProps {
  /** Project ID for fetching map image */
  projectId: string;
  /** Project name */
  name: string;
  /** Optional project description */
  description?: string;
  /** Optional address line */
  addressLine?: string;
  /** Optional city */
  city?: string;
  /** Optional zip code */
  zipCode?: string;
  /** Optional state */
  state?: string;
  /** Optional action buttons to display */
  actionButtons?: Array<{
    key: string;
    label: string;
    badge?: number;
    onClick: () => void;
  }>;
  /** Optional additional fields to display */
  additionalFields?: AdditionalField[];
  /** Optional WhatsApp group invite link */
  whatsappGroupLink?: string;
}

/**
 * Project information section with title, action buttons, location, and description. Displayed at
 * the top of the scrollable content card.
 */
export function ProjectInfo({
  projectId,
  name,
  description,
  addressLine,
  city,
  zipCode,
  state,
  actionButtons,
  additionalFields,
  whatsappGroupLink,
}: ProjectInfoProps) {
  const { t } = useTranslation();
  const MAX_DESCRIPTION_LENGTH = 150;
  const descriptionModalRef = useRef<HTMLIonModalElement>(null);

  // Fetch map image from backend
  const { data: mapImage } = useQuery({
    queryKey: ["project", projectId, "map-image"],
    queryFn: () =>
      api.project.getMapImage(projectId, {
        width: 600,
        height: 120,
        zoom: 15,
        scale: 2,
      }),
    enabled: !!addressLine,
    staleTime: 24 * 60 * 60 * 1000, // Cache for 24 hours
  });

  const handleNavigateToLocation = async () => {
    // Same string as the shift screen builds, from the same function — a street number without a
    // town is not an address, and there is no reason for two screens to disagree about that.
    const fullAddress = formatFullAddress({ addressLine, zipCode, city, state });

    if (!fullAddress) {
      return;
    }

    try {
      await openNativeNavigation(fullAddress, {
        label: name,
        mode: "driving",
      });
    } catch (error) {
      logger.error("Failed to open navigation:", error);
      toast.error(t("common:error.open-navigation"));
    }
  };

  const handleCopyWhatsappLink = async () => {
    if (!whatsappGroupLink) {
      return;
    }
    try {
      await Clipboard.write({ string: whatsappGroupLink });
      toast.success("Link gekopieerd");
    } catch (error) {
      logger.error("Failed to copy WhatsApp link:", error);
      toast.error(t("common:error.copy-link"));
    }
  };

  return (
    <>
      {/* Project title */}
      <div className="px-4 py-4">
        <h1 className="text-2xl font-bold text-txt-primary">{name}</h1>
      </div>

      {/* Action buttons */}
      {actionButtons && actionButtons.length > 0 && (
        <div className="flex flex-col gap-3 px-4 pb-4">
          {actionButtons.map((button) => (
            <ActionButton
              key={button.key}
              badge={button.badge}
              label={button.label}
              onClick={button.onClick}
            />
          ))}
        </div>
      )}

      {/* Additional fields */}
      {additionalFields && additionalFields.length > 0 && (
        <div className="mt-1 px-4">
          {additionalFields.map((field) => {
            const IconComponent = getAdditionalFieldIcon(field.icon);
            return (
              <div key={field.id} className="flex w-full items-center gap-3 py-3 transition-colors">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-surface-primary">
                  <IconComponent className="h-5 w-5 text-txt-brand" />
                </div>
                <div className="flex flex-1 flex-col text-left text-sm font-semibold">
                  <span className="text-txt-primary">{field.title}</span>
                  {field.description && (
                    <span className="font-medium text-txt-secondary">
                      <LinkedText text={field.description} />
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* WhatsApp group - directly under the additional fields, same row style.
          The link itself is shown as a fallback and can be copied or opened. */}
      {whatsappGroupLink && (
        <div className="mt-1 px-4">
          <div className="flex w-full items-center gap-3 py-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-surface-primary">
              <IonIcon icon={logoWhatsapp} className="text-xl text-txt-brand" />
            </div>
            {/* Tap the label to open the group (reliable on iOS/Android) */}
            <button
              type="button"
              onClick={() => openExternalUrl(whatsappGroupLink)}
              className="flex min-w-0 flex-1 flex-col text-left text-sm font-semibold"
            >
              <span className="text-txt-primary">WhatsApp-groep</span>
              <span className="truncate font-medium text-txt-secondary">{whatsappGroupLink}</span>
            </button>
            {/* Copy link */}
            <button
              type="button"
              onClick={handleCopyWhatsappLink}
              aria-label="Kopieer link"
              className="flex h-8 w-8 shrink-0 items-center justify-center"
            >
              <IonIcon icon={copyOutline} className="text-xl text-txt-brand" />
            </button>
            {/* Open link */}
            <button
              type="button"
              onClick={() => openExternalUrl(whatsappGroupLink)}
              aria-label="Open WhatsApp-groep"
              className="flex h-8 w-8 shrink-0 items-center justify-center"
            >
              <IonIcon icon={openOutline} className="text-xl text-txt-brand" />
            </button>
          </div>
        </div>
      )}

      {/* Location with map preview */}
      {addressLine && (
        <div className="mt-1 px-4">
          {/* Clickable location info */}
          <button
            type="button"
            onClick={handleNavigateToLocation}
            className="flex w-full items-center gap-3 py-3 transition-colors hover:bg-surface-secondary active:bg-surface-tertiary"
          >
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-surface-primary">
              <IonIcon icon={locationOutline} className="text-xl text-txt-brand" />
            </div>
            <div className="flex flex-1 flex-col text-left text-sm font-semibold">
              <span className="text-txt-primary">{t("common:location.title")}</span>
              <span className="font-medium text-txt-secondary">{addressLine}</span>
            </div>
            <div className="flex h-8 w-8 shrink-0 items-center justify-center">
              <IonIcon icon={navigateOutline} className="text-xl text-txt-brand" />
            </div>
          </button>

          {/* Static map preview - tappable to open navigation */}
          {mapImage?.data && (
            <button
              type="button"
              onClick={handleNavigateToLocation}
              className="relative mt-4 w-full overflow-hidden rounded-xl shadow-sm transition-all hover:shadow-md active:scale-[0.98]"
            >
              <img
                src={`data:${mapImage.data.contentType};base64,${mapImage.data.image}`}
                alt={`Map showing ${addressLine}`}
                className="h-[120px] w-full object-cover"
                loading="lazy"
              />
              {/* Overlay gradient for better text readability */}
              <div className="from-black/10 absolute inset-0 bg-gradient-to-t to-transparent" />
              {/* Navigation hint overlay */}
              <div className="bg-surface-translucent absolute bottom-3 right-3 flex items-center gap-1.5 rounded-full px-3 py-1.5 shadow-md backdrop-blur-sm">
                <IonIcon icon={navigateOutline} className="text-base text-brand-500" />
                <span className="text-xs font-semibold text-txt-primary">Route</span>
              </div>
            </button>
          )}
        </div>
      )}

      {/* Description */}
      {description && (
        <div className="mt-4 px-4">
          {description.length > MAX_DESCRIPTION_LENGTH ? (
            <>
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-txt-secondary">
                {/* De afgekapte tekst gaat door linkify, niet het origineel: anders wijst een
                    link verder dan wat er staat. `truncated` zorgt er daarbovenop voor dat een
                    URL die door de knip is doorgesneden als tekst blijft staan, want die zou naar
                    een half adres wijzen. Wie de hele beschrijving wil, tikt op "Meer informatie"
                    hieronder, en daar staat hij compleet. */}
                <LinkedText text={description.slice(0, MAX_DESCRIPTION_LENGTH)} truncated />
                ...
              </p>
              <button
                type="button"
                onClick={() => descriptionModalRef.current?.present()}
                className="hover:*: mt-3 w-full rounded-lg bg-white px-4 py-2.5 text-center text-sm font-medium text-txt-primary transition-colors"
                style={{
                  borderWidth: "1.5px",
                  borderStyle: "solid",
                  borderColor: "var(--color-bordercolor-primary)",
                }}
              >
                Meer informatie
              </button>
            </>
          ) : (
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-txt-secondary">
              <LinkedText text={description} />
            </p>
          )}
        </div>
      )}

      {/* Description Modal */}
      {description && description.length > MAX_DESCRIPTION_LENGTH && (
        <ModalSheetAutoHeight ref={descriptionModalRef}>
          <div className="flex min-h-[60vh] flex-col gap-6 p-6">
            {/* Header */}
            <div className="flex items-start justify-between">
              <h2 className="text-xl font-bold text-txt-primary">Beschrijving</h2>
              <button
                type="button"
                onClick={() => descriptionModalRef.current?.dismiss()}
                className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-surface-secondary"
              >
                <IonIcon icon={closeOutline} className="text-2xl text-txt-secondary" />
              </button>
            </div>

            {/* Full description */}
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-txt-secondary">
              <LinkedText text={description} />
            </p>
          </div>
        </ModalSheetAutoHeight>
      )}
    </>
  );
}
